import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react'
import { Routes, Route, Navigate, useNavigate, useParams, useSearchParams, Outlet, useLocation } from 'react-router-dom'
import { LoginForm } from './components/LoginForm'
import { RegisterForm } from './components/RegisterForm'
import { ForgotPasswordForm } from './components/ForgotPasswordForm'
import { ResetPasswordForm } from './components/ResetPasswordForm'
import { InvoiceProcessing } from './components/InvoiceProcessing'
import { InvoicesDashboard } from './components/InvoicesDashboard'
import { InvoiceMatching } from './components/InvoiceMatching'
import { ProcessedFilesPage } from './components/ProcessedFilesPage'
import { ClientManager } from './components/ClientManager'
import { SessionWarning } from './components/SessionWarning'
import { UserManagementPanel } from './components/AdminUserManagement'
import { UserProfile } from './components/UserProfile'
import { SuperstateLogo } from './components/SuperstateLogo'
import { InvoiceReviewSection } from './screens/SaldoReview/sections/InvoiceReviewSection/InvoiceReviewSection'
import { SaldoGenerate } from './screens/SaldoGenerate/SaldoGenerate'
import { useSessionChecker } from './hooks/useSessionChecker'
import { apiClient } from './lib/api'
import { LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppSidebar } from './components/AppSidebar'
import { SidebarProvider, useSidebar } from './contexts/SidebarContext'
import { showSuccessToast, showErrorToast } from './components/NotificationToast'
import { Toaster } from './components/ui/toaster'
import { TooltipProvider } from './components/ui/tooltip'
import { AIInstructionsPage } from './screens/AIInstructionsPage'
import { SystemPromptsPage } from './screens/SystemPromptsPage'
import LearningsPage from './screens/LearningsPage'
import { FileExplorerPage } from './screens/FileManager/FileExplorerPage'
import { TaxRequestsPage } from './screens/FileManager/TaxRequestsPage'
import { BrainDemoPage } from './screens/BrainDemo/BrainDemoPage'
import { DecisionsPage } from './screens/BrainDemo/DecisionsPage'
import { isBrainDemoEnabled } from './screens/BrainDemo/flag'

// User types
interface User {
  id: string
  email: string
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: 'admin' | 'regular' | 'super_admin'
  status: 'active' | 'pending' | 'disabled'
  first_login_at?: string
  created_at: string
  updated_at: string
}

// Review context types
interface ReviewContext {
  type: 'selected' | 'all_pending'
  invoiceIds: string[]
}

// Review session utilities
const REVIEW_SESSIONS_KEY = 'review_sessions'

interface ReviewSession {
  id: string
  context: ReviewContext
  createdAt: number
  expiresAt: number
  initialTotal: number
  completedCount: number
}

const createReviewSession = (context: ReviewContext): string => {
  const sessionId = `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const session: ReviewSession = {
    id: sessionId,
    context,
    createdAt: Date.now(),
    expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
    initialTotal: context.invoiceIds.length,
    completedCount: 0
  }

  // Get existing sessions
  const existingSessions = getStoredReviewSessions()

  // Add new session
  existingSessions[sessionId] = session

  // Clean up expired sessions
  cleanupExpiredSessions(existingSessions)

  // Store updated sessions
  localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(existingSessions))

  return sessionId
}

const getReviewSession = (sessionId: string): ReviewSession | null => {
  const sessions = getStoredReviewSessions()
  const session = sessions[sessionId]

  if (!session || session.expiresAt < Date.now()) {
    // Session expired or doesn't exist
    if (session) {
      delete sessions[sessionId]
      localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(sessions))
    }
    return null
  }

  return session
}

const getStoredReviewSessions = (): Record<string, ReviewSession> => {
  try {
    const stored = localStorage.getItem(REVIEW_SESSIONS_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

const cleanupExpiredSessions = (sessions: Record<string, ReviewSession>): void => {
  const now = Date.now()
  Object.keys(sessions).forEach(sessionId => {
    if (sessions[sessionId].expiresAt < now) {
      delete sessions[sessionId]
    }
  })
}

const initializeReviewSessions = (): void => {
  try {
    const sessions = getStoredReviewSessions()
    const cleanedSessions = { ...sessions }
    cleanupExpiredSessions(cleanedSessions)

    // Only update localStorage if sessions were cleaned up
    if (Object.keys(cleanedSessions).length !== Object.keys(sessions).length) {
      localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(cleanedSessions))
    }
  } catch (error) {
    console.warn('Failed to initialize review sessions:', error)
  }
}

// Authentication Context
interface AuthContext {
  user: User | null
  token: string | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  logout: () => void
  loading: boolean
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  isActive: () => boolean
}

const AuthContext = createContext<AuthContext | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

// Auth Provider Component
function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    // Try to restore user from localStorage on initial load
    const storedUser = localStorage.getItem('user_data')
    return storedUser ? JSON.parse(storedUser) : null
  })
  const [token, setToken] = useState<string | null>(() => {
    // Restore token from localStorage
    return localStorage.getItem('access_token')
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const storedToken = localStorage.getItem('access_token')
    const storedUser = localStorage.getItem('user_data')

    if (storedToken && !token) {
      // Token exists in localStorage but not in state - restore it
      setToken(storedToken)
      // Also restore user data if available
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser))
        } catch (e) {
          console.warn('Failed to parse stored user data:', e)
          localStorage.removeItem('user_data')
        }
      }
    } else if (token && !user) {
      // Token exists but no user data - verify with backend
      verifyTokenWithRetry(token)
    } else if (!token) {
      // No token - user is not authenticated
      setUser(null)
    }
  }, [token, user])

  const verifyTokenWithRetry = async (authToken: string, retryCount = 0) => {
    try {
      const userData = await apiClient.get('/auth/me') as User
      setUser(userData)
      // Store user data in localStorage for hot reload persistence
      localStorage.setItem('user_data', JSON.stringify(userData))
    } catch (error) {
      console.error('Token verification failed:', error)

      // Retry logic for temporary backend issues
      if (retryCount < 3) {
        console.log(`Retrying token verification (${retryCount + 1}/3)...`)
        setTimeout(() => verifyTokenWithRetry(authToken, retryCount + 1), 1000 * (retryCount + 1))
        return
      }

      // Max retries reached - logout
      console.warn('Max retries reached, logging out')
      logout()
    }
  }

  const login = async (email: string, password: string, rememberMe = false) => {
    setLoading(true)
    try {
      const data = await apiClient.post('/auth/login', { email, password, remember_me: rememberMe }) as any
      setToken(data.access_token)
      localStorage.setItem('access_token', data.access_token)

      // Store refresh token for session persistence
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }

      setUser(data.user)
      // Store user data for hot reload persistence
      localStorage.setItem('user_data', JSON.stringify(data.user))

      // Track persistence preference for session monitoring
      localStorage.setItem('remember_me', rememberMe.toString())

      // Set session expiry based on refresh token expiration
      if (data.refresh_expires_in) {
        const expiryTime = Date.now() + data.refresh_expires_in * 1000
        localStorage.setItem('session_expiry', expiryTime.toString())
      }
    } finally {
      setLoading(false)
    }
  }

  const register = async (email: string, password: string, name: string) => {
    setLoading(true)
    try {
      const data = await apiClient.post('/auth/register', { email, password, full_name: name }) as any
      setToken(data.access_token)
      localStorage.setItem('access_token', data.access_token)

      // Store refresh token for session persistence
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }

      setUser(data.user)
      // Store user data for hot reload persistence
      localStorage.setItem('user_data', JSON.stringify(data.user))

      // Set session expiry based on refresh token expiration
      if (data.refresh_expires_in) {
        const expiryTime = Date.now() + data.refresh_expires_in * 1000
        localStorage.setItem('session_expiry', expiryTime.toString())
      }
    } finally {
      setLoading(false)
    }
  }

  const requestPasswordReset = async (email: string) => {
    setLoading(true)
    try {
      await apiClient.post('/auth/forgot-password', { email })
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (token: string, newPassword: string) => {
    setLoading(true)
    try {
      await apiClient.post('/auth/reset-password', { token, new_password: newPassword })
    } finally {
      setLoading(false)
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    setLoading(true)
    try {
      await apiClient.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
    } finally {
      setLoading(false)
    }
  }

  const logout = async (redirectToLogin = true) => {
    // Try to revoke the refresh token on the server
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refresh_token: refreshToken })
      } catch (error) {
        // Ignore errors - we're logging out anyway
        console.warn('Failed to revoke refresh token:', error)
      }
    }

    setUser(null)
    setToken(null)
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token') // Clean up refresh token
    localStorage.removeItem('user_data') // Clean up user data
    localStorage.removeItem('session_expiry') // Clean up any persistent session data
    localStorage.removeItem('remember_me')
    if (redirectToLogin) {
      window.location.href = '/login' // Force full page reload to clear any stale state
    }
  }

  // Helper functions for role and status checking
  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin' || false
  const isSuperAdmin = () => user?.role === 'super_admin' || false
  const isActive = () => user?.status === 'active' || false

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      register,
      requestPasswordReset,
      resetPassword,
      changePassword,
      logout,
      loading,
      isAdmin,
      isSuperAdmin,
      isActive
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!token || !user) {
      navigate('/login', { replace: true })
    }
  }, [token, user, navigate])

  if (!token || !user) {
    return null // Will redirect via useEffect
  }

  return <>{children}</>
}

// Navigation Component
function Navigation() {
  const { user, logout } = useAuth()
  const { warning, extendSession, dismissWarning } = useSessionChecker()

  return (
    <>
      {/* Session Warning */}
      {warning.show && (
        <div className="bg-orange-50 border-b border-orange-200 px-6 py-2">
          <div className="page-shell">
            <SessionWarning
              message={warning.message}
              timeRemaining={warning.timeRemaining}
              onExtend={extendSession}
              onDismiss={dismissWarning}
            />
          </div>
        </div>
      )}

      <nav className="bg-primary shadow-sm border-b border-primary-foreground/20">
        <div className="page-shell px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-primary-foreground">
                <SuperstateLogo variant="#0000FF" width="205" height="14" />
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-primary-foreground/80">Welcome, {user?.full_name || user?.email}</span>
              <Button
                variant="ghost"
                onClick={logout}
                className="flex items-center space-x-2 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

// Auth Pages
function LoginPage() {
  const [currentView, setCurrentView] = useState<'login' | 'register' | 'forgot-password'>('login')
  const [error, setError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const { token, login, register, requestPasswordReset, loading } = useAuth()
  const navigate = useNavigate()

  const handleLogin = async (email: string, password: string, rememberMe?: boolean) => {
    try {
      setError('')
      await login(email, password, rememberMe)
      navigate('/invoices/dashboard')
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unable to sign in. Please try again.'
      setError(message)
    }
  }

  const handleRegister = async (email: string, password: string, name: string) => {
    try {
      setError('')
      await register(email, password, name)
      navigate('/invoices/dashboard')
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.'
      setError(message)
    }
  }

  const handleForgotPassword = async (email: string) => {
    try {
      setError('')
      await requestPasswordReset(email)
      setShowSuccess(true)
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unable to send reset instructions. Please try again.'
      setError(message)
    }
  }

  const resetToLogin = () => {
    setCurrentView('login')
    setError('')
    setShowSuccess(false)
  }

  // If already authenticated, go to main app
  if (token) {
    return <Navigate to="/invoices/dashboard" replace />
  }

  return (
    <div className="min-h-screen bg-login-gradient flex flex-col items-center justify-center py-12 px-6">
      {/* Logo */}
      <div className="mb-8">
        <SuperstateLogo className="h-[25px] w-[255px]" variant="#FFFFFF" />
      </div>

      {currentView === 'login' && (
        <LoginForm
          onLogin={handleLogin}
          onSwitchToRegister={() => setCurrentView('register')}
          onForgotPassword={() => setCurrentView('forgot-password')}
          loading={loading}
          error={error}
        />
      )}

      {currentView === 'register' && (
        <RegisterForm
          onRegister={handleRegister}
          onSwitchToLogin={() => setCurrentView('login')}
          loading={loading}
          error={error}
        />
      )}

      {currentView === 'forgot-password' && (
        <ForgotPasswordForm
          onRequestReset={handleForgotPassword}
          onBackToLogin={resetToLogin}
          loading={loading}
          error={error}
          success={showSuccess}
        />
      )}
    </div>
  )
}

// Password Reset Page (for email links)
function PasswordResetPage() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const { resetPassword, loading } = useAuth()
  const navigate = useNavigate()

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token')
    }
  }, [token])

  const handleResetComplete = async (newPassword: string) => {
    if (!token) {
      setError('Invalid reset token')
      return
    }

    try {
      setError('')
      await resetPassword(token, newPassword)
      setSuccess(true)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-main flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Invalid Reset Link</h2>
            <p className="text-gray-600 mb-6">This password reset link is invalid or has expired.</p>
            <Button onClick={handleBackToLogin}>Back to Sign In</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-main flex items-center justify-center py-12 px-6">
      <ResetPasswordForm
        token={token}
        onResetComplete={handleResetComplete}
        onBackToLogin={handleBackToLogin}
        loading={loading}
        error={error}
        success={success}
      />
    </div>
  )
}

// Main Application Pages

function InvoicesPage() {
  const navigate = useNavigate()

  const handleInvoiceReview = (invoiceId: string, context?: ReviewContext) => {
    sessionStorage.setItem('processingReturnUrl', window.location.pathname + window.location.search)

    const searchParams = new URLSearchParams()

    if (context) {
      // Create a session and store the context
      const sessionId = createReviewSession(context)
      searchParams.set('sessionId', sessionId)
    }

    const queryString = searchParams.toString()
    const url = queryString ? `/review/${invoiceId}?${queryString}` : `/review/${invoiceId}`
    navigate(url)
  }

  return (
    <div className="page-shell">
      <InvoiceProcessing onInvoiceReview={handleInvoiceReview} />
    </div>
  )
}

function ClientsPage() {
  const handleClientSelect = (clientId: string) => {
    // For future enhancement - navigate to client details
    console.log('Selected client:', clientId)
  }

  return (
    <div className="page-shell">
      <ClientManager onClientSelect={handleClientSelect} />
    </div>
  )
}


function SettingsLayout() {
  return (
    <div className="page-shell space-y-6">
      <div>
        <Outlet />
      </div>
    </div>
  )
}

function AdminUsersPage() {
  const { isAdmin } = useAuth()

  // Additional role check for security
  if (!isAdmin()) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">Users</h2>
      </div>


      <UserManagementPanel />
    </div>
  )
}

function InvoiceMatchingPage() {
  return (
    <div className="min-h-screen bg-neutral-50">
      <InvoiceMatching />
    </div>
  )
}

function UserProfilePage() {
  const { user, changePassword, loading } = useAuth()

  if (!user) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
        <p className="text-gray-500">You must be logged in to access this page.</p>
      </div>
    )
  }

  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    await changePassword(currentPassword, newPassword)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">Profile</h2>
      </div>

      <div className="max-w-2xl">
        <UserProfile
          user={user}
          onPasswordChange={handlePasswordChange}
          loading={loading}
        />
      </div>
    </div>
  )
}

function ReviewPage() {
  const navigate = useNavigate()
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const [searchParams] = useSearchParams()
  const [invoice, setInvoice] = useState<any>(null)
  const [filteredInvoiceList, setFilteredInvoiceList] = useState<any[]>([])
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState<number>(-1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const { token } = useAuth()
  const searchParamsKey = searchParams.toString()
  const pollTimerRef = useRef<number | null>(null)

  // Note: InvoiceReviewSection now calculates remaining invoices from session context

  const fetchInvoiceList = useCallback(async () => {
    try {
      // Get token from localStorage for authentication
      const authToken = localStorage.getItem('access_token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }

      // Fetch invoice list
      const invoicesResponse = await apiClient.get('/invoices/') as any
      const fetchedInvoices = invoicesResponse.invoices || []

      // Parse URL parameters for review session
      const sessionId = searchParams.get('sessionId')

      let filteredInvoices = []
      let reviewSession: ReviewSession | null = null

      if (sessionId) {
        // Load review context from session storage
        reviewSession = getReviewSession(sessionId)
        if (reviewSession) {
          const { type, invoiceIds } = reviewSession.context
          if (type === 'selected' || type === 'all_pending') {
            // Filter to invoices in the session list (don't filter by status so
            // already-reviewed invoices stay in the batch for navigation)
            filteredInvoices = fetchedInvoices.filter((inv: any) => invoiceIds.includes(inv.id))
          }
        }
      }

      setFilteredInvoiceList(filteredInvoices)

      // Find current invoice index in filtered list
      if (invoiceId) {
        const currentIndex = filteredInvoices.findIndex((inv: any) => inv.id === invoiceId)
        setCurrentInvoiceIndex(currentIndex)
      }

      // Update pending review count based on filtered list or remaining session invoices
      const remainingCount = reviewSession?.context.invoiceIds.length ?? filteredInvoices.length
      if (remainingCount > 0) {
        localStorage.setItem('pendingReviewCount', remainingCount.toString())
      } else {
        localStorage.removeItem('pendingReviewCount')
      }

      return filteredInvoices
    } catch (err: any) {
      console.error('Error fetching invoice list:', err)
      return []
    }
  }, [invoiceId, searchParamsKey])

  const fetchInvoiceData = useCallback(async () => {
    console.log('Invoice ID from params:', invoiceId)

    if (!invoiceId) {
      setError('No invoice ID provided')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Fetch invoice list first
      await fetchInvoiceList()

      // Get token from localStorage for authentication
      const authToken = localStorage.getItem('access_token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }

      // Fetch invoice details using main endpoint
      const invoiceData = await apiClient.get(`/invoices/${invoiceId}`) as any

      const invoiceObj = {
        ...invoiceData.invoice,
        extractedData: invoiceData.extracted_data || [],
        currency_rate_info: invoiceData.currency_rate_info || null,
        currency_representations: invoiceData.currency_representations || [],
      }
      setInvoice(invoiceObj)
      setIsProcessing(invoiceObj.status === 'processing' || invoiceObj.status === 'uploaded')
    } catch (err: any) {
      setError(err.message || 'Failed to load invoice data')
      console.error('Error loading invoice:', err)
    } finally {
      setLoading(false)
    }
  }, [fetchInvoiceList, invoiceId, token])

  // Poll when invoice is processing/uploaded
  useEffect(() => {
    if (!isProcessing || !invoiceId) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
      return
    }

    pollTimerRef.current = window.setInterval(async () => {
      try {
        const data = await apiClient.get(`/invoices/${invoiceId}`) as any
        const status = data.invoice?.status
        if (status && status !== 'processing' && status !== 'uploaded') {
          setIsProcessing(false)
          setInvoice({
            ...data.invoice,
            extractedData: data.extracted_data || [],
            currency_rate_info: data.currency_rate_info || null,
            currency_representations: data.currency_representations || [],
          })
        }
      } catch {
        // Silently retry on next interval
      }
    }, 3000)

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [isProcessing, invoiceId])

  useEffect(() => {
    void fetchInvoiceData()
  }, [fetchInvoiceData])

  // Lightweight refresh that updates invoice state without showing the loading spinner
  const refreshInvoiceSilently = useCallback(async () => {
    if (!invoiceId) return
    try {
      const invoiceData = await apiClient.get(`/invoices/${invoiceId}`) as any
      setInvoice({
        ...invoiceData.invoice,
        extractedData: invoiceData.extracted_data || [],
        currency_rate_info: invoiceData.currency_rate_info || null,
        currency_representations: invoiceData.currency_representations || [],
      })
    } catch (err) {
      console.error('Silent refresh failed:', err)
    }
  }, [invoiceId])

  const handleApprove = async () => {
    try {
      // Update invoice status to "reviewed"
      await apiClient.put(`/invoices/${invoice?.id}/status`, {
        status: 'reviewed'
      })

      // Remove this invoice from the session context
      const sessionId = searchParams.get('sessionId')
      if (sessionId && invoice?.id) {
        const session = getReviewSession(sessionId)
        if (session) {
          // Remove the current invoice from the session's invoice list
          const updatedContext: ReviewContext = {
            ...session.context,
            invoiceIds: session.context.invoiceIds.filter(id => id !== invoice.id)
          }

          // Update the session in localStorage
          const sessions = getStoredReviewSessions()
          const storedSession = sessions[sessionId]
          if (storedSession) {
            const currentCompleted = typeof storedSession.completedCount === 'number' ? storedSession.completedCount : 0
            const fallbackInitialTotal = currentCompleted + updatedContext.invoiceIds.length + 1
            const normalizedInitialTotal = typeof storedSession.initialTotal === 'number' && storedSession.initialTotal > 0
              ? storedSession.initialTotal
              : fallbackInitialTotal

            storedSession.context = updatedContext
            storedSession.initialTotal = normalizedInitialTotal
            storedSession.completedCount = Math.min(currentCompleted + 1, normalizedInitialTotal)

            localStorage.setItem(REVIEW_SESSIONS_KEY, JSON.stringify(sessions))
          }

          // Update the filtered list to reflect the change
          setFilteredInvoiceList(prev => {
            const updated = prev.filter(inv => inv.id !== invoice.id)
            const remainingCount = updatedContext.invoiceIds.length
            if (remainingCount > 0) {
              localStorage.setItem('pendingReviewCount', remainingCount.toString())
            } else {
              localStorage.removeItem('pendingReviewCount')
            }

            const returnUrl = sessionStorage.getItem('processingReturnUrl') || '/invoices/processing'

            // Handle navigation after approval
            if (updated.length === 0) {
              // No more invoices in session, redirect to list
              navigate(returnUrl)
            } else {
              // Navigate to the next invoice (same index, since we removed current)
              const nextInvoice = updated[Math.min(currentInvoiceIndex, updated.length - 1)]
              if (nextInvoice) {
                navigate(`/review/${nextInvoice.id}?sessionId=${sessionId}`)
              } else {
                navigate(returnUrl)
              }
            }

            return updated
          })
        } else {
          navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
        }
      } else {
        navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      }

      // Show success message
      showSuccessToast('Invoice marked as reviewed!')

      // Navigation is handled inside the setFilteredInvoiceList callback above
    } catch (err) {
      console.error('Error approving invoice:', err)
      showErrorToast(`Error updating invoice status: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleSkip = () => {
    // Single invoice - go back to list
    if (filteredInvoiceList.length <= 1) {
      navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      return
    }

    // Batch mode - always navigate to next (wraps around via handleNavigateToNext)
    handleNavigateToNext()
  }

  const handlePrevious = () => {
    // If only one invoice is selected, go to the list page
    if (filteredInvoiceList.length <= 1) {
      navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')
      return
    }

    // Navigate to the previous invoice
    handleNavigateToPrevious()
  }

  const handleNavigateToPrevious = () => {
    if (filteredInvoiceList.length === 0) return

    let previousIndex = currentInvoiceIndex - 1
    if (previousIndex < 0) {
      // Go to the last invoice
      previousIndex = filteredInvoiceList.length - 1
    }

    const previousInvoice = filteredInvoiceList[previousIndex]
    if (previousInvoice) {
      // Preserve the session ID in the URL
      const sessionId = searchParams.get('sessionId')
      const searchParamsObj = new URLSearchParams()
      if (sessionId) {
        searchParamsObj.set('sessionId', sessionId)
      }
      const queryString = searchParamsObj.toString()
      const url = queryString ? `/review/${previousInvoice.id}?${queryString}` : `/review/${previousInvoice.id}`
      navigate(url)
    }
  }

  const handleNavigateToNext = () => {
    if (filteredInvoiceList.length === 0) return

    let nextIndex = currentInvoiceIndex + 1
    if (nextIndex >= filteredInvoiceList.length) {
      // Loop back to the first invoice
      nextIndex = 0
    }

    const nextInvoice = filteredInvoiceList[nextIndex]
    if (nextInvoice) {
      // Preserve the session ID in the URL
      const sessionId = searchParams.get('sessionId')
      const searchParamsObj = new URLSearchParams()
      if (sessionId) {
        searchParamsObj.set('sessionId', sessionId)
      }
      const queryString = searchParamsObj.toString()
      const url = queryString ? `/review/${nextInvoice.id}?${queryString}` : `/review/${nextInvoice.id}`
      navigate(url)
    }
  }

  if (loading) {
    return (
      <div className="page-shell">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-300">Loading invoice...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="page-shell">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            ← Back to Invoice Processing
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="mb-6">
        <button
          onClick={() => navigate(sessionStorage.getItem('processingReturnUrl') || '/invoices/processing')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          ← Back to Invoice Processing
        </button>
        <h1 className="text-2xl font-bold text-foreground mb-1 mt-4 page-main-heading">Invoice Review</h1>
      </div>

      <InvoiceReviewSection
        invoice={invoice}
        isProcessing={isProcessing}
        onInvoiceRefresh={fetchInvoiceData}
        onInvoiceRefreshSilent={refreshInvoiceSilently}
        onProcessingStarted={() => setIsProcessing(true)}
        onFieldPatch={(updatedField: Record<string, unknown>) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              extractedData: prev.extractedData.map((f: any) =>
                f.id === updatedField.id ? { ...f, ...updatedField } : f
              ),
            };
          });
        }}
        onRepresentationUpdate={(updatedRepr) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            return {
              ...prev,
              currency_representations: prev.currency_representations?.map((r: any) =>
                r.id === updatedRepr.id ? updatedRepr : r
              ) ?? [],
            };
          });
        }}
        onLineItemsUpdated={(extractedData, representation, additionalMetadata) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            const updated: any = {
              ...prev,
              extractedData: extractedData,
            };
            if (representation) {
              updated.currency_representations = prev.currency_representations?.map((r: any) =>
                r.id === representation.id ? representation : r
              ) ?? [representation];
            }
            if (additionalMetadata) {
              updated.additional_metadata = additionalMetadata;
            }
            return updated;
          });
        }}
        onErpMetadataPatched={(lineItemGroup, metadata) => {
          setInvoice((prev: any) => {
            if (!prev) return prev;
            const prevMeta = prev.additional_metadata ?? {};
            const erp = { ...(prevMeta.erp ?? {}) };
            const lineItems = [...(erp.line_items ?? [])];
            const idx = lineItems.findIndex((li: any) => li.line_item_group === lineItemGroup);
            const entry = { line_item_group: lineItemGroup, ...metadata };
            if (idx >= 0) {
              lineItems[idx] = entry;
            } else {
              lineItems.push(entry);
            }
            erp.line_items = lineItems;
            return {
              ...prev,
              additional_metadata: { ...prevMeta, erp },
            };
          });
        }}
        onFieldUpdate={async (fieldId, newValue) => {
          if (!invoice) return;

          try {
            const response = await apiClient.fetch(`/invoices/${invoice.id}/fields/${fieldId}`, {
              method: 'PUT',
              body: JSON.stringify({
                field_value: newValue,
                is_corrected: true
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.detail || `Failed to update field: ${response.statusText}`);
            }

            const result = await response.json();

            // Partial state update — patch the field in-place instead of refetching
            const updatedField = result.field;
            if (updatedField) {
              setInvoice((prev: any) => {
                if (!prev) return prev;
                const next: any = {
                  ...prev,
                  extractedData: prev.extractedData.map((f: any) =>
                    f.id === updatedField.id ? { ...f, ...updatedField } : f
                  ),
                };
                // When the backend returns updated currency_rate_info (e.g. after
                // invoice_date change), patch it into the invoice state so the
                // currency section refreshes without a full page reload.
                if (result.currency_rate_info) {
                  next.currency_rate_info = result.currency_rate_info;
                }
                return next;
              });
            }

            showSuccessToast('Field updated successfully!');

          } catch (error) {
            console.error('Error updating field:', error);
            showErrorToast(`Error updating field: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
        onApprove={handleApprove}
        onSkip={handleSkip}
        onPrevious={handlePrevious}
        currentInvoiceIndex={currentInvoiceIndex}
        totalInvoices={filteredInvoiceList.length || (invoice ? 1 : 0)}
      />
    </div>
  )
}

// Landing Page - commented out as unused
// function LandingPage() {
// const navigate = useNavigate()
// const { token } = useAuth()

// if (token) {
//   return <Navigate to="/upload" replace />
// }

// return (
//   <div className="min-h-screen bg-main">
//     <div className="container mx-auto px-4 py-8">
//       <header className="text-center mb-12">
//         <h1 className="text-5xl font-bold text-white mb-4">
//           Superstate Dashboard
//         </h1>
//         <div className="flex justify-center space-x-4">
//           <button
//             onClick={() => navigate('/login')}
//             className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
//           >
//             Get Started
//           </button>
//           <button
//             onClick={() => navigate('/login')}
//             className="bg-white hover:bg-main text-gray-700 font-medium py-3 px-6 rounded-lg border border-gray-300 transition-colors"
//           >
//             Sign In
//           </button>
//         </div>
//       </header>

//       <div className="max-w-4xl mx-auto">
//         {/* Features Grid */}
//         <div className="grid md:grid-cols-3 gap-8 mb-12">
//           <div className="text-center p-6 bg-white rounded-lg shadow-sm">
//             <Upload className="h-12 w-12 text-blue-600 mx-auto mb-4" />
//             <h3 className="text-lg font-semibold mb-2">Smart Upload</h3>
//             <p className="text-black">
//               Drag & drop invoices with real-time processing
//             </p>
//           </div>

//           <div className="text-center p-6 bg-white rounded-lg shadow-sm">
//             <FileText className="h-12 w-12 text-green-600 mx-auto mb-4" />
//             <h3 className="text-lg font-semibold mb-2">AI Extraction</h3>
//             <p className="text-black">
//               Automatic data extraction with confidence scoring
//             </p>
//           </div>

//           <div className="text-center p-6 bg-white rounded-lg shadow-sm">
//             <BarChart3 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
//             <h3 className="text-lg font-semibold mb-2">Live Dashboard</h3>
//             <p className="text-black">
//               Real-time progress tracking and analytics
//             </p>
//           </div>
//         </div>
//       </div>

//       <div className="mt-12 text-center text-sm text-gray-500">
//         <p>Superstate Saldo Dashboard v1.0.0</p>
//       </div>
//     </div>
//   </div>
// )
// }

// Main Layout Content Component (uses useSidebar hook)
function MainLayoutContent({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const isReviewPage = location.pathname.startsWith('/review/')
  const { setIsCollapsed, isCollapsed } = useSidebar()
  const prevIsReviewPageRef = useRef(false)
  const savedStateBeforeReviewRef = useRef<boolean | null>(null)

  useEffect(() => {
    // Only collapse when first entering the review page
    if (isReviewPage && !prevIsReviewPageRef.current) {
      // Save current state before collapsing
      savedStateBeforeReviewRef.current = isCollapsed
      setIsCollapsed(true)
    }
    // Restore previous state when leaving the review page
    else if (!isReviewPage && prevIsReviewPageRef.current) {
      if (savedStateBeforeReviewRef.current !== null) {
        setIsCollapsed(savedStateBeforeReviewRef.current)
        savedStateBeforeReviewRef.current = null
      }
    }
    prevIsReviewPageRef.current = isReviewPage
  }, [isReviewPage, setIsCollapsed, isCollapsed])

  return (
    <div className="min-h-screen bg-main">
      <Navigation />
      <div className="flex">
        <AppSidebar />
        <main className="flex-1 px-6 py-6">
          {/* <Breadcrumbs /> */}
          {/* <div className="border-b border-neutral-200 -mx-6 mb-6"></div> */}
          {children}
        </main>
      </div>
    </div>
  )
}

// Main Layout for authenticated users
function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <MainLayoutContent>{children}</MainLayoutContent>
    </SidebarProvider>
  )
}

// Main App Component
function App() {
  // Initialize review sessions cleanup on app start
  useEffect(() => {
    initializeReviewSessions()
  }, [])

  return (
    <AuthProvider>
      <TooltipProvider delayDuration={300}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />

          {/* Protected Routes */}

          <Route path="/invoices/dashboard" element={
            <ProtectedRoute>
              <MainLayout>
                <InvoicesDashboard />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/invoices/processing" element={
            <ProtectedRoute>
              <MainLayout>
                <InvoicesPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/clients" element={
            <ProtectedRoute>
              <MainLayout>
                <ClientsPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/files" element={
            <ProtectedRoute>
              <MainLayout>
                <FileExplorerPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/files/tax-requests" element={
            <ProtectedRoute>
              <MainLayout>
                <TaxRequestsPage />
              </MainLayout>
            </ProtectedRoute>
          } />


          <Route path="/settings" element={
            <ProtectedRoute>
              <MainLayout>
                <SettingsLayout />
              </MainLayout>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<UserProfilePage />} />
            <Route path="ai-instructions" element={<AIInstructionsPage />} />
            <Route path="prompts" element={<SystemPromptsPage />} />
            <Route path="admin" element={<AdminUsersPage />} />
            <Route path="learnings" element={<LearningsPage />} />
          </Route>

          <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/admin/users" element={<Navigate to="/settings/admin" replace />} />

          <Route path="/reports/:conversationId?" element={
            <ProtectedRoute>
              <MainLayout>
                <SaldoGenerate />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/reconciliation" element={
            <ProtectedRoute>
              <MainLayout>
                <InvoiceMatchingPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/reconciliation/files" element={
            <ProtectedRoute>
              <MainLayout>
                <ProcessedFilesPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/invoices/matching" element={
            <ProtectedRoute>
              <MainLayout>
                <InvoiceMatchingPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          <Route path="/review/:invoiceId?" element={
            <ProtectedRoute>
              <MainLayout>
                <ReviewPage />
              </MainLayout>
            </ProtectedRoute>
          } />

          {isBrainDemoEnabled && (
            <>
              <Route path="/demo/brain" element={
                <ProtectedRoute>
                  <MainLayout>
                    <BrainDemoPage />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/demo/brain/present" element={
                <ProtectedRoute>
                  <BrainDemoPage presentMode />
                </ProtectedRoute>
              } />
              <Route path="/demo/decisions" element={
                <ProtectedRoute>
                  <MainLayout>
                    <DecisionsPage />
                  </MainLayout>
                </ProtectedRoute>
              } />
            </>
          )}

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster />
      </TooltipProvider>
    </AuthProvider>
  )
}

export default App
